import type { SortingState } from '@tanstack/react-table';
import { uniq } from 'es-toolkit/array';
import { use, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ManagementPageLayout } from '../../components/management/ManagementPageLayout';
import { TRPCContext } from '../../contexts/TRPC';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { cn } from '../../lib/utils';
import { canAccessAdminPages } from '../../utils/roleUtils';
import { AutomatedReviewSection } from './components/AutomatedReviewSection';
import { ExternalMetricsSection } from './components/ExternalMetricsSection';
import { IndividualAppSection } from './components/IndividualAppSection';
import { OnboardingKybSection } from './components/OnboardingKybSection';
import { PublicationSummarySection } from './components/PublicationSummarySection';
import { PublishedAppsSection } from './components/PublishedAppsSection';
import { ReportingCoverageBanner } from './components/ReportingCoverageBanner';
import { ReportingTabs } from './components/ReportingTabs';
import {
  createInitialDateRange,
  createPresetDateRange,
  filterReportingApps,
  formatNullableDate,
  type ReportingDateRangeState,
  type ReportingLifecycleFilterValue,
  type ReportingPresetKey,
  type ReportingTabKey,
  sortReportingApps,
  sortReportingAppsAlphabetically,
  toEndOfLocalDayIso,
  toStartOfLocalDayIso,
} from './reportingUtils';
import { useReportingAppDetail } from './useReportingAppDetail';
import { useReportingData } from './useReportingData';
import type { ReportingApp } from './types';

const APP_LIST_DEFAULT_SORT: SortingState = [
  { id: 'latestPublishedAtInRange', desc: true },
];
const REPORTING_LIFECYCLE_OPTIONS: Array<{
  value: ReportingLifecycleFilterValue;
  label: string;
}> = [
  { value: 'all', label: 'All activity' },
  { value: 'new', label: 'New launches' },
  { value: 'existing', label: 'Version updates' },
];

export function ReportingPage() {
  const { authState, canRender } = useRequireAuth({
    requireRole: 'admin',
  });
  const trpc = use(TRPCContext);

  usePageTitle('Reporting | Publishing Portal');

  const [activeTab, setActiveTab] = useState<ReportingTabKey>('summary');
  const [dateRange, setDateRange] = useState<ReportingDateRangeState>(
    createInitialDateRange,
  );
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [lifecycleFilter, setLifecycleFilter] =
    useState<ReportingLifecycleFilterValue>('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [appListSorting, setAppListSorting] = useState<SortingState>(
    APP_LIST_DEFAULT_SORT,
  );
  const hasValidDateRange =
    dateRange.fromInput.length > 0 && dateRange.toInput.length > 0;

  const {
    data,
    externalMetrics,
    error,
    sectionError,
    isLoading,
    sectionLoading,
    sectionStatus,
    availability,
  } = useReportingData({
    trpc,
    dateFrom: hasValidDateRange
      ? toStartOfLocalDayIso(dateRange.fromInput)
      : null,
    dateTo: hasValidDateRange ? toEndOfLocalDayIso(dateRange.toInput) : null,
    enabled: hasValidDateRange,
  });
  const hasPendingSections = Object.values(sectionLoading).some(Boolean);
  const isStoreMetricsLoading = sectionStatus.store === 'loading';
  const isStoreMetricsUnavailable = sectionStatus.store === 'failed';

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (sectionError) {
      toast.error(sectionError);
    }
  }, [sectionError]);

  const allApps = data?.apps ?? [];
  const alphabetizedApps = sortReportingAppsAlphabetically(allApps);
  const appsInRange = allApps.filter((app) => app.publishedInRange);
  const filteredAppsInRange = filterReportingApps(appsInRange, {
    search: searchFilter,
    category: categoryFilter,
    lifecycle: lifecycleFilter,
  });
  const individualApps = filterReportingApps(alphabetizedApps, {
    search: '',
    category: 'all',
    lifecycle: lifecycleFilter,
  });

  useEffect(() => {
    if (individualApps.length === 0) {
      setSelectedAppId(null);
      return;
    }

    const selectedStillExists = individualApps.some(
      (app) => app.dappId === selectedAppId,
    );
    if (!selectedStillExists) {
      setSelectedAppId(individualApps[0].dappId);
    }
  }, [individualApps, selectedAppId]);

  const sortedAppList = sortReportingApps(filteredAppsInRange, appListSorting);
  const categoryOptions = buildCategoryOptions(appsInRange);
  const selectedApp =
    individualApps.find((app) => app.dappId === selectedAppId) ??
    individualApps[0] ??
    null;
  const individualEmptyMessage =
    allApps.length === 0
      ? 'No apps with publication activity are available yet.'
      : 'No apps match the current filters.';

  const focusApp = (app: ReportingApp) => {
    setSelectedAppId(app.dappId);
    setActiveTab('individual');
  };

  const {
    data: selectedAppDetail,
    error: selectedAppDetailError,
    isLoading: isSelectedAppDetailLoading,
  } = useReportingAppDetail({
    trpc,
    androidPackage: selectedApp?.packageName ?? null,
    enabled:
      activeTab === 'individual' &&
      !isStoreMetricsLoading &&
      !isStoreMetricsUnavailable &&
      (selectedApp?.packageName?.length ?? 0) > 0,
  });

  useEffect(() => {
    if (selectedAppDetailError) {
      toast.error(selectedAppDetailError);
    }
  }, [selectedAppDetailError]);

  if (!canRender) {
    return null;
  }

  return (
    <ManagementPageLayout
      authState={authState}
      canAccess={canAccessAdminPages}
      className="flex-1 overflow-y-auto"
    >
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 md:px-8 md:py-10">
        <header className="space-y-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wider text-neutral-500">
                Admin · Insights
              </div>
              <h1 className="mt-1.5 text-2xl font-semibold tracking-tight text-white">
                Reporting
              </h1>
              <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-neutral-500">
                Publication history, publisher onboarding, store coverage,
                reviews, and external service telemetry.
              </p>
            </div>
            {data && (
              <div className="flex flex-col items-start gap-1 text-xs text-neutral-500 sm:items-end">
                {hasPendingSections ? (
                  <span
                    role="status"
                    aria-label="Loading reporting sections"
                    className="inline-flex items-center gap-1.5"
                  >
                    <span className="size-1.5 rounded-full bg-teal-400 motion-safe:animate-pulse" />
                    <span>Loading reporting sections…</span>
                  </span>
                ) : (
                  <>
                    {data.lastSyncedAt && (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="size-1.5 rounded-full bg-teal-400" />
                        <span className="tabular-nums">
                          Synced {formatNullableDate(data.lastSyncedAt)}
                        </span>
                      </span>
                    )}
                    <span className="tabular-nums">
                      Generated {formatNullableDate(data.generatedAt)}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <DateRangeControls
              dateRange={dateRange}
              onPresetSelect={(preset) =>
                setDateRange(createPresetDateRange(preset))
              }
              onDateChange={(nextRange) =>
                setDateRange({
                  ...nextRange,
                  preset: 'custom',
                })
              }
            />

            <ReportingTabs activeTab={activeTab} onChange={setActiveTab} />
          </div>

          <ReportingCoverageBanner
            availability={availability}
            isLoading={isLoading || hasPendingSections}
          />
        </header>

        {isLoading && (
          <div className="rounded-xl border border-neutral-800/70 bg-neutral-900/30 px-5 py-12 text-center text-sm text-neutral-500">
            Loading reporting data…
          </div>
        )}

        {!isLoading && error && !data && (
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.05] px-4 py-3 text-sm text-amber-300">
            {error}
          </div>
        )}

        {!isLoading && data && activeTab === 'summary' && (
          <PublicationSummarySection
            summary={data.summary}
            summaryByCategory={data.summaryByCategory}
            apps={appsInRange}
            isStoreMetricsLoading={isStoreMetricsLoading}
            isStoreMetricsUnavailable={isStoreMetricsUnavailable}
            onAppFocus={focusApp}
          />
        )}

        {!isLoading &&
          data &&
          activeTab === 'onboarding' &&
          (externalMetrics.onboardingKyb ? (
            <OnboardingKybSection metrics={externalMetrics.onboardingKyb} />
          ) : (
            <ReportingSectionLoading
              isLoading={sectionLoading.onboardingKyb}
              label="Loading onboarding data..."
            />
          ))}

        {!isLoading &&
          data &&
          activeTab === 'automatedReview' &&
          (externalMetrics.automatedReview ? (
            <AutomatedReviewSection metrics={externalMetrics.automatedReview} />
          ) : (
            <ReportingSectionLoading
              isLoading={sectionLoading.automatedReview}
              label="Loading automated review data..."
            />
          ))}

        {!isLoading &&
          data &&
          activeTab === 'externalMetrics' &&
          (externalMetrics.backendIngestor ? (
            <ExternalMetricsSection
              backendIngestor={externalMetrics.backendIngestor}
            />
          ) : (
            <ReportingSectionLoading
              isLoading={sectionLoading.backendIngestor}
              label="Loading backend ingestor data..."
            />
          ))}

        {!isLoading &&
          data &&
          activeTab === 'appList' &&
          (isStoreMetricsLoading || isStoreMetricsUnavailable ? (
            <ReportingSectionLoading
              isLoading={isStoreMetricsLoading}
              label="Loading app store data..."
              unavailableLabel="App store enrichment is unavailable for this range."
            />
          ) : (
            <PublishedAppsSection
              apps={sortedAppList}
              categoryOptions={categoryOptions}
              lifecycleOptions={REPORTING_LIFECYCLE_OPTIONS}
              searchValue={searchFilter}
              categoryValue={categoryFilter}
              lifecycleValue={lifecycleFilter}
              sorting={appListSorting}
              onSearchChange={setSearchFilter}
              onCategoryChange={setCategoryFilter}
              onLifecycleChange={setLifecycleFilter}
              onSortingChange={setAppListSorting}
              onAppFocus={focusApp}
            />
          ))}

        {!isLoading &&
          data &&
          activeTab === 'individual' &&
          (isStoreMetricsLoading || isStoreMetricsUnavailable ? (
            <ReportingSectionLoading
              isLoading={isStoreMetricsLoading}
              label="Loading app store data..."
              unavailableLabel="App store enrichment is unavailable for this range."
            />
          ) : (
            <IndividualAppSection
              apps={individualApps}
              selectedAppId={selectedAppId}
              onSelectApp={setSelectedAppId}
              lifecycleOptions={REPORTING_LIFECYCLE_OPTIONS}
              lifecycleValue={lifecycleFilter}
              onLifecycleChange={setLifecycleFilter}
              emptyMessage={individualEmptyMessage}
              selectedAppDetail={selectedAppDetail}
              selectedAppDetailError={selectedAppDetailError}
              isSelectedAppDetailLoading={isSelectedAppDetailLoading}
            />
          ))}
      </div>
    </ManagementPageLayout>
  );
}

function ReportingSectionLoading({
  isLoading,
  label,
  unavailableLabel = 'Section data is unavailable for this range.',
}: {
  isLoading: boolean;
  label: string;
  unavailableLabel?: string;
}) {
  return (
    <div className="rounded-xl border border-neutral-800/70 bg-neutral-900/30 px-5 py-12 text-center text-sm text-neutral-500">
      {isLoading ? label : unavailableLabel}
    </div>
  );
}

function buildCategoryOptions(apps: ReportingApp[]) {
  const values = uniq(apps.map((app) => app.category)).sort((left, right) =>
    left.localeCompare(right),
  );

  return [
    { value: 'all', label: 'All categories' },
    ...values.map((value) => ({ value, label: value })),
  ];
}

function DateRangeControls({
  dateRange,
  onPresetSelect,
  onDateChange,
}: {
  dateRange: ReportingDateRangeState;
  onPresetSelect: (preset: Exclude<ReportingPresetKey, 'custom'>) => void;
  onDateChange: (dateRange: ReportingDateRangeState) => void;
}) {
  const presets: Array<{
    key: Exclude<ReportingPresetKey, 'custom'>;
    label: string;
  }> = [
    { key: '7d', label: '7D' },
    { key: '30d', label: '30D' },
    { key: '90d', label: '90D' },
    { key: '1y', label: '1Y' },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex rounded-lg border border-neutral-800/70 bg-neutral-950/40 p-0.5">
        {presets.map((preset) => (
          <button
            key={preset.key}
            type="button"
            onClick={() => onPresetSelect(preset.key)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium tabular-nums transition-colors',
              dateRange.preset === preset.key
                ? 'bg-neutral-800 text-neutral-100'
                : 'text-neutral-500 hover:text-neutral-300',
            )}
          >
            {preset.label}
          </button>
        ))}
      </div>
      <div className="inline-flex items-center gap-1 rounded-lg border border-neutral-800/70 bg-neutral-950/40 px-1 py-0.5">
        <input
          type="date"
          aria-label="From date"
          value={dateRange.fromInput}
          onChange={(event) =>
            onDateChange({ ...dateRange, fromInput: event.target.value })
          }
          className="rounded-md bg-transparent px-1.5 py-1 text-xs text-neutral-300 tabular-nums [color-scheme:dark] focus:outline-none focus:ring-1 focus:ring-teal-500/40"
        />
        <span aria-hidden="true" className="text-neutral-600">
          →
        </span>
        <input
          type="date"
          aria-label="To date"
          value={dateRange.toInput}
          onChange={(event) =>
            onDateChange({ ...dateRange, toInput: event.target.value })
          }
          className="rounded-md bg-transparent px-1.5 py-1 text-xs text-neutral-300 tabular-nums [color-scheme:dark] focus:outline-none focus:ring-1 focus:ring-teal-500/40"
        />
      </div>
    </div>
  );
}
